import React, { useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth, UserButton, SignInButton } from "@clerk/react";
import { isAdminUser } from "../data/adminUsers";

type NavBarProps = {
  isMenuOpen: boolean;
  setIsMenuOpen: (open: boolean) => void;
};

const NavBar: React.FC<NavBarProps> = ({ isMenuOpen, setIsMenuOpen }) => {
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const { isSignedIn, isLoaded, has, userId } = useAuth();
  const isCaptain = isLoaded && (has({ plan: "vessel_vibes_captain" }) || isAdminUser(userId));

  return (
    <>
      {/* Top Nav */}
      <nav className="absolute top-0 left-0 right-0 z-10 bg-black/80 backdrop-blur-md">
        <div className="container mx-auto px-3 py-2 flex items-center justify-between relative">
          <div className="flex items-center space-x-2">
            <Link
              to="/"
              className="flex items-center space-x-2 hover:opacity-90 transition"
            >
              <img
                src="/favicon.ico"
                alt="Logo"
                className="h-8 w-9 rounded-full"
              />
              <span className="futuristic-title">CruiseRadar</span>
              <div className="subtitle">by Vessel Vibes</div>
            </Link>
          </div>

          {/* Desktop */}
          <div className="hidden md:flex items-center space-x-6">
            {/* <a href="/" className="hover:text-green-400 transition-colors">
              <span className="social-title">Home</span>
            </a> */}

            {/* <a href="/blog" className="hover:text-green-400 transition-colors">
              <span className="social-title">Blog</span>
            </a> */}

            <a href="/" className="hover:text-green-400 transition-colors">
              <span className="social-title">Map</span>
            </a>

            <a href="/fleet" className="hover:text-green-400 transition-colors">
              <span className="social-title">Fleet</span>
            </a>

            <a href="/race" className="hover:text-green-400 transition-colors">
              <span className="social-title">🏁 Race</span>
            </a>
            {/* <a href="/bridge" className="hover:text-green-400 transition-colors"><span className="social-title">🛳 Bridge</span></a> */}{/* POC */}

            <a href="/about" className="hover:text-green-400 transition-colors">
              <span className="social-title">About</span>
            </a>

            {/* Contact Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowContactDropdown((prev) => !prev)}
                className="hover:text-green-400 transition-colors"
              >
                <span className="social-title">Contact</span>
              </button>
              {showContactDropdown && (
                <div className="absolute right-0 mt-2 bg-black border border-white/20 rounded shadow-lg px-4 py-2 text-sm text-white whitespace-nowrap z-50">
                  vesselvibesmedia@gmail.com
                </div>
              )}
            </div>

            {/* Auth */}
            {!isLoaded ? (
              <div className="w-8 h-8" />
            ) : isSignedIn ? (
              <div className="flex items-center gap-3">
                {isCaptain ? (
                  <span className="flex items-center gap-1.5 text-cyan-400">
                    <span className="text-xs font-bold tracking-wider">⚓ CAPTAIN</span>
                  </span>
                ) : (
                  <a href="/captain" className="social-title bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-3 py-1 rounded transition-colors text-xs">
                    Captain Access
                  </a>
                )}
                <UserButton />
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <a href="/captain" className="social-title bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-3 py-1 rounded transition-colors text-xs">
                  Captain Access
                </a>
                <SignInButton mode="modal">
                  <button className="social-title text-gray-400 hover:text-white text-xs transition-colors">
                    Sign In
                  </button>
                </SignInButton>
              </div>
            )}
          </div>

          {/* Hamburger Menu */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="absolute top-16 left-0 right-0 bg-black/95 backdrop-blur-md z-20 md:hidden">
          <div className="container mx-auto px-4 py-4 flex flex-col items-center space-y-4 text-center">
            {/* <a href="/blog" className="hover:text-green-400 transition-colors">
              <span className="social-title">Blog</span>
            </a> */}
            <a href="/" className="hover:text-green-400 transition-colors">
              <span className="social-title">Map</span>
            </a>
            <a href="/fleet" className="hover:text-green-400 transition-colors">
              <span className="social-title">Fleet</span>
            </a>
            <a href="/race" className="hover:text-green-400 transition-colors">
              <span className="social-title">🏁 Race</span>
            </a>
            <div className="text-white text-sm mt-2">
              Contact: vesselvibesmedia@gmail.com
            </div>
            {!isLoaded ? null : isSignedIn ? (
              <div className="flex flex-col items-center gap-2">
                {isCaptain ? (
                  <span className="flex items-center gap-1.5 text-cyan-400">
                    <span className="text-xs font-bold tracking-wider">⚓ CAPTAIN</span>
                  </span>
                ) : (
                  <a href="/captain" className="social-title bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-4 py-2 rounded transition-colors text-sm">
                    Captain Access
                  </a>
                )}
                <UserButton />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <a href="/captain" className="social-title bg-cyan-500 hover:bg-cyan-400 text-black font-semibold px-4 py-2 rounded transition-colors text-sm">
                  Captain Access
                </a>
                <SignInButton mode="modal">
                  <button className="social-title text-gray-400 hover:text-white text-xs transition-colors">
                    Sign In
                  </button>
                </SignInButton>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NavBar;
